import { EItemScreenMode } from './stores/useItemScreenStore';
import { TMap } from '@shared/types';
import { AccessType } from '@modules/access/types';
import splitExtension from './helpers/splitExtension.helper';

export const AUTO_EDIT_QUERY_PARAM = 'autoEdit';
export const AUTO_EDIT_QUERY_VALUE = 'true';
export const PERIODIC_REVIEW_QUERY_PARAM = 'periodicReview';
export const PERIODIC_REVIEW_QUERY_VALUE = 'open';

// Used to map the `EItemScreenMode` values to user-friendly strings
export const SCREEN_MODE_TO_PARAM_MAP: TMap<EItemScreenMode, string> = {
  [EItemScreenMode.CHANGES]: 'changes',
  [EItemScreenMode.COMMENTS]: 'comments',
  [EItemScreenMode.MOCK_AI_FINDINGS]: 'ai-findings',
  [EItemScreenMode.PRE_CHANGE_IMPACT_ANALYSIS]: 'pre-change-impact-analysis',
  [EItemScreenMode.POST_CHANGE_IMPACT_ANALYSIS]: 'post-change-impact-analysis',
  [EItemScreenMode.IMPACT_ANALYSIS_SUMMARY]: 'impact-analysis-summary',
  [EItemScreenMode.AI]: 'ai',
};

export const ITEMS_NAV_DRAWER_WIDTH_PX = 320;

// Right padding reserved so the page content stays centered when the nav drawer AND a sidebar are both open.
export const OPEN_SIDEBAR_PADDING_RIGHT_PX = 160;

export const FULL_PATH_SETUP_ITEM = Object.freeze({
  _id: 'setup',
  name: 'Setup',
  disabled: true,
  accessRight: AccessType.NONE,
});

export const ITEMS_TABLE_COLUMN_IDS = ['name', 'modified', 'latestVersionId', 'approvedVersionId', 'status', 'authors'];

export const SINGLE_SNAPSHOT_TABLE_COLUMN_IDS = ['name', 'modified', 'status', 'latestVersionId', 'authors'];

export const LIBRARY_TABLE_COLUMN_IDS = ['name', 'preview', 'modified', 'latestVersionId', 'authors'];

export const COUNTRIES_OR_SNAPSHOTS_ROOT_TABLE_COLUMN_IDS = ['name', 'modified', 'authors'];

export const COUNTRIES_OR_SNAPSHOTS_SUB_TABLE_COLUMN_IDS = ['name', 'products', 'modified', 'authors'];

export const ITEM_MIMETYPES = {
  image: [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/bmp',
    'image/webp',
    'image/tiff',
    'image/svg+xml',
    'image/x-icon',
    'image/heif',
    'image/heic',
    'image/avif',
  ],
  pdf: [
    'application/pdf',
    'application/x-pdf',
    'application/acrobat',
    'applications/vnd.pdf',
    'text/pdf',
    'text/x-pdf',
  ],
  docx: [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword', // .doc
    'application/vnd.ms-word.document.macroEnabled.12',
  ],
  xlsx: [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel.sheet.macroEnabled.12',
    'application/vnd.ms-excel.addin.macroEnabled.12',
    'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
  ],
  pptx: [
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
    'application/vnd.ms-powerpoint.addin.macroEnabled.12',
    'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
    'application/vnd.ms-powerpoint.slideshow.macroEnabled.12',
  ],
  zip: ['application/zip', 'application/x-zip-compressed', 'multipart/x-zip', 'application/x-compressed'],
  dotx: [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
    'application/vnd.ms-word.template.macroEnabled.12',
  ],
  xltx: [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
    'application/vnd.ms-excel.template.macroEnabled.12',
  ],
  potx: [
    'application/vnd.openxmlformats-officedocument.presentationml.template',
    'application/vnd.ms-powerpoint.template.macroEnabled.12',
  ],
};

/**
 * Maps file extensions to user-friendly labels for display.
 * Used for "Download original {label}" button text.
 */
export const FILE_EXTENSION_LABELS: Record<string, string> = {
  docx: 'Word',
  doc: 'Word',
  html: 'HTML',
  htm: 'HTML',
  pdf: 'PDF',
  xlsx: 'Excel',
  xls: 'Excel',
  pptx: 'PowerPoint',
  ppt: 'PowerPoint',
};

/**
 * Get a user-friendly label for a file based on its extension.
 * @param fileName - The filename with extension
 * @returns User-friendly label (e.g., "Word", "HTML") or "file" as fallback
 */
export const getFileTypeLabel = (fileName: string | undefined): string => {
  if (!fileName) return 'file';
  const [, ext] = splitExtension(fileName);
  return (ext && FILE_EXTENSION_LABELS[ext.toLowerCase()]) || 'file';
};

// ARCHIVE feature
export const ARCHIVE_ENDPOINT = '/items/archived';
export const ARCHIVE_QUERY_KEY = 'archived-items';
export const ARCHIVE_UI_MARGIN_TOP = 200;
export const ARCHIVE_TABLE_ID = 'archive-items-table';

// --- SHARED BETWEEN FRONTEND AND BACKEND ---
// System/temporary files to filter from import
export const IGNORED_FILE_PREFIXES = ['~$', '._'];
export const IGNORED_FILE_NAMES_LOWER_CASE = ['.ds_store', 'thumbs.db', 'desktop.ini'];
